import type { ReactNode } from 'react';
import { Component } from 'react';

import * as Sentry from '@sentry/react';

import { SomethingWentWrong } from './SomethingWentWrong';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  eventId?: string;
  hasError: boolean;
  error?: Error;
}

/**
 * Catches errors in the children components and displays a fallback UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const eventId = Sentry.captureException(error);
    this.setState({ eventId, error });
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <SomethingWentWrong
            sentryEventId={this.state.eventId}
            error={
              this.state.error ?? new Error('Unknown error in ErrorBoundary')
            }
          />
        )
      );
    }

    return this.props.children;
  }
}
